export class APIError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public code?: string
  ) {
    super(detail);
    this.name = 'APIError';
  }

  static async fromResponse(response: Response): Promise<APIError> {
    const status = response.status;
    let detail = 'Unknown error';
    let code: string | undefined;

    try {
      const body = await response.json();
      detail = body?.detail || body?.message || body?.error || 'Unknown error';
      code = body?.code;
    } catch {
      // If response is not JSON, use status text
      detail = response.statusText || 'Unknown error';
    }

    // Specific error handling
    switch (status) {
      case 401:
        return new APIError(status, 'Authentication failed. Check API password.', 'AUTH_FAILED');
      case 403:
        return new APIError(status, 'Access forbidden', 'FORBIDDEN');
      case 404:
        return new APIError(status, 'Resource not found', 'NOT_FOUND');
      case 429:
        return new APIError(status, 'Rate limit exceeded. Please wait a moment.', 'RATE_LIMITED');
      case 500:
        return new APIError(status, 'Server error. Please try again later.', 'SERVER_ERROR');
      case 503:
        return new APIError(status, 'Service unavailable. Please try again later.', 'SERVICE_UNAVAILABLE');
      default:
        return new APIError(status, detail, code);
    }
  }
}

export const handleAPIError = (error: unknown): string => {
  if (error instanceof APIError) {
    switch (error.code) {
      case 'AUTH_FAILED':
        return 'Authentication failed. Please check your API configuration.';
      case 'RATE_LIMITED':
        return 'Too many requests. Please wait a moment.';
      case 'SERVER_ERROR':
        return 'Server error. Please try again later.';
      case 'SERVICE_UNAVAILABLE':
        return 'Service is temporarily unavailable. Please try again later.';
      default:
        return error.detail;
    }
  }
  
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch')) {
      return 'Network error. Please check your connection.';
    }
    return error.message;
  }
  
  return 'An unexpected error occurred';
};